import React, { useRef, useState } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
  Button as ChakraButton,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Flex,
  Spinner,
  Text,
  useToast,
} from '@chakra-ui/react';
import download from 'downloadjs';
import { Button, Heading, Table } from '~/components';
import { useActiveEquipaments } from '~/hooks/useActiveEquipaments';
import { queryClient } from '~/utils/react-query';
import api from '~/utils/api';
import { Card } from '~/components/Card';

export const ActiveEquipaments: React.VFC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast();

  const { data, isLoading, error } = useActiveEquipaments();
  const [isUploading, setUploading] = useState(false);
  const [isDownloading, setDownloading] = useState(false);

  const uploadRef = useRef<HTMLInputElement | null>(null);

  const handleOpen = () => {
    onOpen();
  };

  const handleClose = () => {
    onClose();
  };

  const handleUpload = (_file: File | null) => {
    const form = new FormData();
    form.append('file', _file as Blob);

    setUploading(true);

    const request = api.post('/equipamentos_vigentes/upload/', form);

    request
      .then(() => {
        toast({
          title: `Planilha anexada`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        queryClient.invalidateQueries('activeEquipaments');
      })
      .catch(err => {
        if (err.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (err.response.status === 413) {
          toast({
            title: `Tamanho de requisição excedida`,
            description: 'Limite máximo para tamanho de arquivos é de 5MB',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (err.response.status === 400) {
          // erros de valid

          const errorsValidate = Object.entries(err.response.data);

          errorsValidate.forEach(valErr => {
            console.log(valErr[0]);
            toast({
              title: `Erros ao realizar o upload`,
              description: String(valErr[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        } else {
          toast({
            title: `Erro ao realizar o upload`,
            description: 'Tente novamente',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const handleDownload = () => {
    setDownloading(true);

    api
      .get('/equipamentos_vigentes/download/', {
        responseType: 'blob' as 'json',
      })
      .then(r => {
        if (r.data) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(err => {
        // recuperando o erro do back
        // https://github.com/axios/axios/issues/3779
        err.response.data.text().then((errorStr: string) => {
          const errorJson = JSON.parse(errorStr);
          toast({
            title: `Erro`,
            description: errorJson.detail,
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        });
      })
      .finally(() => setDownloading(false));
  };

  return (
    <>
      <ChakraButton
        fontSize="sm"
        variant="link"
        fontWeight="normal"
        color="inherit"
        textAlign="left"
        justifyContent="normal"
        onClick={handleOpen}
      >
        Equipamentos vigentes
      </ChakraButton>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="lg"
        closeOnOverlayClick={false}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader mb={4}>
            <Heading fontSize="xl">Equipamentos vigentes</Heading>
          </DrawerHeader>

          <DrawerBody>
            <Flex w="100%" mb={4} justify="flex-end">
              <Button
                onClick={() =>
                  uploadRef.current ? uploadRef.current.click() : ''
                }
                disabled={isUploading}
              >
                Anexar planilha
              </Button>
              <input
                id="file"
                type="file"
                onChange={e => {
                  handleUpload(e.target.files ? e.target.files[0] : null);
                  e.target.value = '';
                }}
                accept=".xlsx"
                ref={uploadRef}
                style={{ display: 'none' }}
              />
            </Flex>
            {isLoading ? (
              <Flex justify="center">
                <Spinner />
              </Flex>
            ) : error ? (
              <Flex justify="center">
                <Card
                  content={
                    <Text align="center" padding={4}>
                      Falha ao obter dados dos equipamentos.
                    </Text>
                  }
                  width="100%"
                />
              </Flex>
            ) : data && !data.length ? (
              <Flex justify="center">
                <Card
                  content={
                    <Text align="center" padding={4}>
                      Nenhum arquivo anexado
                    </Text>
                  }
                  width="100%"
                />
              </Flex>
            ) : (
              <Flex flexDir="column">
                <Table type="secondary">
                  <Thead>
                    <Tr>
                      <Th>Nome do arquivo</Th>
                      <Th>Data de atualização</Th>
                      <Th>Usuário</Th>
                      <Th>Ação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data &&
                      data.length &&
                      data.map(upload => (
                        <Tr key={upload.nome_arquivo}>
                          <Td textOverflow="ellipsis" maxWidth="250px">
                            {upload.nome_arquivo || '-'}
                          </Td>
                          <Td>{upload.data_upload_fmt || '-'}</Td>
                          <Td>{upload.nome_usuario || '-'}</Td>
                          <Td>
                            <ChakraButton
                              fontSize="sm"
                              variant="link"
                              fontWeight="normal"
                              color="inherit"
                              textAlign="left"
                              justifyContent="normal"
                              onClick={handleDownload}
                              disabled={isDownloading}
                            >
                              Baixar
                            </ChakraButton>
                          </Td>
                        </Tr>
                      ))}
                  </Tbody>
                </Table>
              </Flex>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};
